import { AuditTrailSchema } from "../types/AuditTrail";
import { BookingSchema } from "../types/Booking";
import { CommentSchema } from "../types/Comment";
import { LocationSchema } from "../types/Location";
import { MemberSchema } from "../types/Member";
import { OrganisationSchema } from "../types/Organisation";
import { RoomSchema } from "../types/Room";
import { createIngestor } from "./createIngestor";
import RemoteAnalyticsAPI from "./remoteAnalyticsApi";
import { RemoteAmenityAPI } from "./remoteAmenityApi";
import RemoteBillingAPI from "./remoteBillingApi";
import RemoteOccurrenceAmenitiesAPI from "./remoteOccurrenceAmenitiesApi";
import RemoteAuditTrailAPI from "./remoteAuditTrailApi";
import RemoteBookingAPI from "./remoteBookingApi";
import RemoteCommentAPI from "./remoteCommentApi";
import RemoteHelpAPI from "./remoteHelpApi";
import RemoteLocationAPI from "./remoteLocationApi";
import RemoteMemberAPI from "./remoteMemberApi";
import RemoteMessageAPI from "./remoteMessageApi";
import RemoteOrganisationAPI from "./remoteOrganisationApi";
import RemoteReportAPI from "./remoteReportApi";
import RemoteRoomAPI from "./remoteRoomApi";
import type { IAnalyticsAPI } from "./analyticsApi";
import type { IAmenityAPI } from "./amenityApi";
import type { IBillingAPI } from "./billingApi";
import type { IOccurrenceAmenitiesAPI } from "./occurrenceAmenitiesApi";
import type { IAuditTrailAPI } from "./auditTrailApi";
import type { IBookingAPI } from "./bookingApi";
import type { ICommentAPI } from "./commentApi";
import type { IHelpAPI } from "./helpApi";
import type { ILocationAPI } from "./locationApi";
import type { IMemberAPI } from "./memberApi";
import type { IMessageAPI } from "./messageApi";
import type { IOrganisationAPI } from "./organisationApi";
import type { IReportAPI } from "./reportApi";
import type { IRoomAPI } from "./roomApi";

export const createLocationAPI = (): ILocationAPI => {
  const api = new RemoteLocationAPI();
  return createIngestor(LocationSchema, api, "Location");
};

export const createMemberAPI = (): IMemberAPI => {
  const api = new RemoteMemberAPI();
  return createIngestor(MemberSchema, api, "Member");
};

export const createBookingAPI = (): IBookingAPI => {
  const api = new RemoteBookingAPI();
  return createIngestor(BookingSchema, api, "Booking");
};

export const createRoomAPI = (): IRoomAPI => {
  const api = new RemoteRoomAPI();
  return createIngestor(RoomSchema, api, "Room");
};

export const createOrganisationAPI = (): IOrganisationAPI => {
  const api = new RemoteOrganisationAPI();
  return createIngestor(OrganisationSchema, api, "Organisation");
};

export const createAuditTrailAPI = (): IAuditTrailAPI => {
  const api = new RemoteAuditTrailAPI();
  return createIngestor(AuditTrailSchema, api, "AuditTrail");
};

export const createCommentAPI = (): ICommentAPI => {
  const api = new RemoteCommentAPI();
  return createIngestor(CommentSchema, api, "Comment");
};

export const createAmenityAPI = (): IAmenityAPI => {
  // Note: We don't wrap with createIngestor because the Amenity API
  // returns an array of Amenity objects from its methods
  return new RemoteAmenityAPI();
};

export const createMessageAPI = (): IMessageAPI => {
  // Note: We don't wrap with createIngestor because the Message API
  // returns different types (Message, Conversation, void) from different methods
  // and the ingestor would try to validate everything as Message objects
  return new RemoteMessageAPI();
};

export const createReportAPI = (): IReportAPI => {
  return new RemoteReportAPI();
};

export const createHelpAPI = (): IHelpAPI => {
  // Note: We don't wrap with createIngestor because the Help API
  // returns different types (HelpCategory, CategoryWithArticles, HelpArticle, etc.)
  // from different methods and the ingestor would try to validate everything as one type
  return new RemoteHelpAPI();
};

export const createAnalyticsAPI = (): IAnalyticsAPI => {
  // Note: We don't wrap with createIngestor because the Analytics API
  // returns custom dashboard data types
  return new RemoteAnalyticsAPI();
};

export const createBillingAPI = (): IBillingAPI => new RemoteBillingAPI();

// Not wrapped in createIngestor: the response is per-date DTOs (Zod-parsed in the remote), not an entity list.
export const createOccurrenceAmenitiesAPI = (): IOccurrenceAmenitiesAPI => new RemoteOccurrenceAmenitiesAPI();
